/** A site by id, searched over the client-loaded sites list. Shows `siteCode — name`. */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { ReactNode } from "react";

import type { Site } from "../../api/client";
import { clientListOptions } from "../../api/queries/useClientRegister";
import { useBootstrap } from "../../api/queries/useBootstrap";
import { CommandPicker } from "./CommandPicker";

export interface SitePickerProps {
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  allowClear?: boolean;
  /** An alternative trigger, e.g. the filter bar's chip. */
  trigger?: ReactNode;
}

export function SitePicker({ value, onChange, allowClear, trigger }: SitePickerProps) {
  const { data: boot } = useBootstrap();
  const tenantKey = boot?.tenant.key ?? null;
  const sites = useQuery(clientListOptions<Site>(tenantKey, "sites", "listSites"));

  const items = useMemo(
    () =>
      (sites.data?.rows ?? []).map((row) => ({
        value: row.id,
        label: `${row.siteCode} — ${row.name}`,
      })),
    [sites.data],
  );

  return (
    <CommandPicker
      value={value}
      onChange={onChange}
      allowClear={allowClear}
      trigger={trigger}
      items={items}
      loading={sites.isPending}
      placeholder="Search sites"
    />
  );
}
